import { createContext, useContext, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { LEVEL_RANK, LEVELS, levelAtLeast } from "@/lib/permissions";

/**
 * iter157 — PermissionsContext
 *
 * SINGLE READ POINT for permission decisions across the app. Backed by
 * `/api/auth/me → permissions_effective` (populated by the backend
 * permission engine). Everything — sidebar, routes, buttons, drawers —
 * MUST route through `usePermission()` / `<PermissionGate>`. No
 * duplicated logic anywhere else.
 *
 * Owners get Full Control on every module unconditionally. That is
 * enforced by the backend resolver — this file simply reads what the
 * server declared.
 */
const PermissionsContext = createContext({
  effective: {},
  isReady: false,
  isOwner: false,
});

export function PermissionsProvider({ children }) {
  const { user } = useAuth();

  const value = useMemo(() => {
    const effective = user?.permissions_effective || {};
    return {
      effective,
      isReady: !!user,
      isOwner: !!user?.is_owner,
    };
  }, [user]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

// Primary hook: returns the level string for a module.
//   const level = usePermission("bookings");
export function usePermission(module) {
  const { effective, isOwner } = useContext(PermissionsContext);
  if (isOwner) return LEVELS.FULL_CONTROL;
  return effective[module] || LEVELS.NO_ACCESS;
}

// Helper hook returning capability booleans for the given module.
//   const { canView, canEdit, canDelete, canCreate, level } = useCapabilities("bookings");
export function useCapabilities(module) {
  const level = usePermission(module);
  return {
    level,
    canView: levelAtLeast(level, LEVELS.VIEW_ONLY),
    canCreate: levelAtLeast(level, LEVELS.EDIT),
    canEdit: levelAtLeast(level, LEVELS.EDIT),
    canDelete: levelAtLeast(level, LEVELS.FULL_CONTROL),
    canExport: levelAtLeast(level, LEVELS.FULL_CONTROL),
  };
}

// Declarative wrapper.
//   <PermissionGate module="bookings" min="edit">
//     <NewButton />
//   </PermissionGate>
export function PermissionGate({ module, min = LEVELS.VIEW_ONLY, children, fallback = null }) {
  const level = usePermission(module);
  if (!levelAtLeast(level, min)) return fallback;
  return children;
}

export { LEVELS, LEVEL_RANK };
